import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {emojiName, isEmojiToken, translateToEmoji} from '../utils/enhancedEmojiTranslator';

const MAX_LENGTH = 500;

// Accent "potions": each generation's orb color comes from the logo palette.
const GENERATIONS = [
  {key: 'boomer', label: 'Boomer', icon: '🏛️', hue: '#ffd970', glow: 'rgba(240,176,40,0.55)'},
  {key: 'genX', label: 'Gen X', icon: '📼', hue: '#86d6c8', glow: 'rgba(40,160,140,0.5)'},
  {key: 'millennial', label: 'Millennial', icon: '✨', hue: '#f6b6d6', glow: 'rgba(225,110,170,0.5)'},
  {key: 'genZ', label: 'Gen Z', icon: '💀', hue: '#b9a3e3', glow: 'rgba(124,98,200,0.55)'},
  {key: 'genAlpha', label: 'Gen Alpha', icon: '🔥', hue: '#ffb07a', glow: 'rgba(240,120,50,0.5)'}
];

const SpeechRecognition = typeof window !== 'undefined'
  ? window.SpeechRecognition || window.webkitSpeechRecognition
  : undefined;

// iPhone/iPad: every browser there runs on WebKit, whose web speech recognition is less reliable
// than the keyboard's own dictation (it can fail to start and stops at the first pause). Speak
// still listens there, one phrase per tap, and points to the keyboard mic as the backup.
const IS_APPLE_TOUCH = typeof navigator !== 'undefined' && (
  /iPad|iPhone|iPod/.test(navigator.userAgent)
  || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) // iPadOS reports as a Mac
);
// Android Chrome repeats earlier phrases in continuous mode, so phones listen one phrase at a time.
const IS_MOBILE = IS_APPLE_TOUCH || /Android|Mobi/i.test(typeof navigator !== 'undefined' ? navigator.userAgent : '');
const KEYBOARD_TIP = 'Tip: for longer messages, the 🎙️ on your keyboard is the most accurate.';
// iPhone/iPad web speech proved too unreliable in testing (mostly "didn't catch that"), so there
// the app relies on the keyboard's own dictation mic instead of a Speak button.
const USE_WEB_SPEECH = Boolean(SpeechRecognition) && !IS_APPLE_TOUCH;

function MicIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="9" y="3" width="6" height="11" rx="3" stroke="currentColor" strokeWidth="2" />
      <path d="M5 11a7 7 0 0 0 14 0M12 18v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function CopyIcon({copied}) {
  return copied ? (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M3 8.5l3 3 7-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ) : (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="5" y="5" width="9" height="9" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <path d="M11 5V3.5A1.5 1.5 0 0 0 9.5 2h-6A1.5 1.5 0 0 0 2 3.5v6A1.5 1.5 0 0 0 3.5 11H5" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

const EmojiTranslator = () => {
  const [input, setInput] = useState('');
  const [mode, setMode] = useState('millennial');
  const [copied, setCopied] = useState(false);
  const [listening, setListening] = useState(false);
  const [micError, setMicError] = useState('');
  const [dictateHint, setDictateHint] = useState(false);
  const [picked, setPicked] = useState(null); // {emoji, name} for the tap-to-name caption
  const textareaRef = useRef(null);
  const recognitionRef = useRef(null);
  const copyTimerRef = useRef(null);
  const hintTimerRef = useRef(null);
  const watchdogRef = useRef(null);
  const drainingRef = useRef(null); // a stopped session still delivering its last words

  // Translate as you type: the engine is local and synchronous, so no button or debounce needed.
  const result = useMemo(() => {
    const message = input.trim();
    if (!message) return null;
    try {
      return translateToEmoji(message, mode);
    } catch (error) {
      console.error('Emoji translation failed:', error);
      return {emojis: [message], emojiChain: message};
    }
  }, [input, mode]);

  useEffect(() => setPicked(null), [result]);

  // Stop the microphone and timers when the component goes away.
  useEffect(() => () => {
    const recognition = recognitionRef.current;
    recognitionRef.current = null;
    try { recognition?.abort(); } catch (error) { /* already stopped */ }
    window.clearTimeout(copyTimerRef.current);
    window.clearTimeout(hintTimerRef.current);
    window.clearTimeout(watchdogRef.current);
  }, []);

  // Let go of the current voice session right away. iOS doesn't always fire "end" after stop(),
  // which used to leave a dead session behind and block every later tap on Speak. Releasing it
  // here (and ignoring its late events) means a new session can always start.
  // 'stop' (the user tapped Listening…) still accepts the last words already spoken;
  // 'abort' (Clear, or a new session) drops them.
  const releaseRecognition = useCallback((how = 'stop') => {
    const recognition = recognitionRef.current;
    window.clearTimeout(watchdogRef.current);
    if (how === 'abort') drainingRef.current = null;
    if (!recognition) return;
    recognitionRef.current = null;
    drainingRef.current = how === 'stop' ? recognition : null;
    setListening(false);
    try {
      if (how === 'abort') recognition.abort();
      else recognition.stop();
    } catch (error) { /* already stopped */ }
    // If it lingers, force it closed so the microphone is free for the next tap.
    window.setTimeout(() => { try { recognition.abort(); } catch (error) { /* gone */ } }, 1500);
  }, []);

  const stopListening = useCallback(() => releaseRecognition('stop'), [releaseRecognition]);

  // iPhone/iPad: after each voice session, point to the keyboard mic for longer dictation.
  const showKeyboardTip = useCallback(() => {
    setDictateHint(true);
    window.clearTimeout(hintTimerRef.current);
    hintTimerRef.current = window.setTimeout(() => setDictateHint(false), 10000);
  }, []);

  const startListening = useCallback(() => {
    if (!USE_WEB_SPEECH) return;
    if (recognitionRef.current) releaseRecognition('abort'); // never let a stale session block a new one
    setMicError('');
    setDictateHint(false);
    const keyboardHint = IS_APPLE_TOUCH ? ' Or tap the 🎙️ on your keyboard.' : '';

    // iOS can't listen while the keyboard is up (its own dictation holds the microphone),
    // so close the keyboard first.
    if (IS_APPLE_TOUCH && document.activeElement === textareaRef.current) textareaRef.current.blur();

    let recognition;
    try {
      recognition = new SpeechRecognition();
    } catch (error) {
      setMicError(`Voice input couldn’t start. Please try again.${keyboardHint}`);
      return;
    }
    recognition.lang = navigator.language || 'en-US';
    recognition.interimResults = true;
    recognition.continuous = !IS_MOBILE;
    recognition.maxAlternatives = 1;

    // Speech is appended after whatever was already typed. Finished phrases are kept once
    // (from resultIndex on, so nothing repeats); the phrase in progress is shown live.
    const base = textareaRef.current?.value.trim() || '';
    const isCurrent = () => recognitionRef.current === recognition;
    const accepts = () => isCurrent() || drainingRef.current === recognition;
    let finished = '';
    let heard = false;
    let failed = false;

    // iOS sometimes "listens" but never delivers words. If nothing arrives in time, stop and say so.
    const armWatchdog = () => {
      window.clearTimeout(watchdogRef.current);
      watchdogRef.current = window.setTimeout(() => {
        if (!isCurrent()) return;
        releaseRecognition('abort');
        if (heard) { if (IS_APPLE_TOUCH) showKeyboardTip(); }
        else setMicError(`Didn’t catch that. Tap Speak and try again.${keyboardHint}`);
      }, heard ? 4000 : 8000);
    };

    recognition.onresult = (event) => {
      if (!accepts()) return; // a cleared or replaced session must not write into the box
      heard = true;
      if (IS_MOBILE) armWatchdog();
      let inProgress = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const phrase = event.results[i][0]?.transcript || '';
        if (event.results[i].isFinal) finished = `${finished} ${phrase}`;
        else inProgress = `${inProgress} ${phrase}`;
      }
      const spoken = `${finished} ${inProgress}`.replace(/\s+/g, ' ').trim();
      const combined = [base, spoken].filter(Boolean).join(' ');
      setInput(combined.slice(0, MAX_LENGTH));
    };
    recognition.onerror = (event) => {
      if (!isCurrent() || event.error === 'aborted') return;
      failed = true;
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        setMicError(`Microphone access is blocked. Allow it in your browser settings to speak.${keyboardHint}`);
      } else if (event.error === 'no-speech') {
        setMicError(`Didn’t catch that. Tap Speak and try again.${keyboardHint}`);
      } else {
        setMicError(`Voice input stopped unexpectedly. Please try again.${keyboardHint}`);
      }
    };
    recognition.onend = () => {
      if (drainingRef.current === recognition) drainingRef.current = null;
      if (!isCurrent()) return; // already released (Clear, Stop, or a newer session)
      window.clearTimeout(watchdogRef.current);
      recognitionRef.current = null;
      setListening(false);
      if (!IS_APPLE_TOUCH || failed) return;
      // iOS sometimes ends without hearing anything and without reporting an error.
      if (!heard) setMicError(`Didn’t catch that. Tap Speak and try again.${keyboardHint}`);
      else showKeyboardTip();
    };

    try {
      recognitionRef.current = recognition;
      recognition.start();
      setListening(true);
      if (IS_MOBILE) armWatchdog();
    } catch (error) {
      recognitionRef.current = null;
      setMicError(`Voice input couldn’t start. Please try again.${keyboardHint}`);
    }
  }, [releaseRecognition, showKeyboardTip]);

  const clear = () => {
    releaseRecognition('abort');
    setDictateHint(false);
    setInput('');
    setCopied(false);
    setMicError('');
    // On iPhone, raising the keyboard would hold the microphone and block the next Speak.
    if (!IS_APPLE_TOUCH) textareaRef.current?.focus();
  };

  const copy = async () => {
    if (!result?.emojiChain) return;
    try {
      await navigator.clipboard.writeText(result.emojiChain);
      setCopied(true);
      window.clearTimeout(copyTimerRef.current);
      copyTimerRef.current = window.setTimeout(() => setCopied(false), 1500);
    } catch (error) {
      setCopied(false);
    }
  };

  const generation = GENERATIONS.find((item) => item.key === mode) || GENERATIONS[2];

  return (
    <div className="ea-card">
      <div className="ea-field">
        <label htmlFor="ea-message" className="ea-label">{USE_WEB_SPEECH || IS_APPLE_TOUCH ? 'Type or speak your message' : 'Type your message'}</label>
        <div className={`ea-input${listening ? ' is-listening' : ''}`}>
          <textarea
            id="ea-message"
            ref={textareaRef}
            value={input}
            maxLength={MAX_LENGTH}
            onChange={(event) => setInput(event.target.value)}
            placeholder="Type here… like “I walked my dog to the park and bought ice cream”"
            rows={3}
          />
          {input && (
            <button type="button" className="ea-clear" onClick={clear} aria-label="Clear message">
              <span aria-hidden="true">
                <svg width="12" height="12" viewBox="0 0 12 12"><path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
              </span>
            </button>
          )}
          {/* Tapping the empty footer also lands in the text box. */}
          <div
            className="ea-input-footer"
            onClick={(event) => { if (!event.target.closest('button')) textareaRef.current?.focus(); }}
          >
            {USE_WEB_SPEECH ? (
              <span className="ea-input-actions">
                <button
                  type="button"
                  className={`ea-mic${listening ? ' is-listening' : ''}`}
                  onClick={listening ? stopListening : startListening}
                  aria-pressed={listening}
                  aria-label={listening ? 'Stop listening' : 'Speak your message'}
                >
                  {listening && <span className="ea-mic-pulse" aria-hidden="true" />}
                  <MicIcon />
                  <span>{listening ? 'Listening…' : 'Speak'}</span>
                </button>
                {!input && !listening && <span className="ea-or-type" aria-hidden="true">or just type</span>}
              </span>
            ) : IS_APPLE_TOUCH ? (
              <span className="ea-keyboard-tip">
                <span aria-hidden="true">🎙️</span> To talk, tap the mic on your keyboard
              </span>
            ) : <span />}
            <span className="ea-count">{MAX_LENGTH - input.length} left</span>
          </div>
        </div>
        {micError && <p className="ea-mic-error" role="status">{micError}</p>}
        {dictateHint && (
          <p className="ea-mic-hint" role="status">{KEYBOARD_TIP}</p>
        )}
      </div>

      <div className="ea-field">
        <span className="ea-label" id="ea-story-label">Emoji story</span>
        <div className="ea-story" role="region" aria-labelledby="ea-story-label">
          <button
            type="button"
            className="ea-copy"
            onClick={copy}
            disabled={!result}
            aria-label={copied ? 'Copied' : 'Copy emoji story'}
          >
            <CopyIcon copied={copied} />
          </button>
          <p className="ea-story-text" aria-live="polite">
            {result ? result.emojis.map((token, index) => (
              isEmojiToken(token) ? (
                <button
                  key={`${token}-${index}`}
                  type="button"
                  className="ea-token"
                  onClick={() => setPicked({emoji: token, name: emojiName(token) || 'emoji'})}
                  aria-label={emojiName(token) || token}
                >
                  {token}
                </button>
              ) : (
                <span key={`${token}-${index}`} className="ea-word">{token}</span>
              )
            )) : (
              <span className="ea-story-empty">Your emoji story appears here as you type.</span>
            )}
          </p>
          <p className="ea-caption">
            {picked ? `${picked.emoji}  ${picked.name}` : result ? 'Tap an emoji to see its name.' : ''}
          </p>
        </div>
      </div>

      <div className="ea-field">
        <span className="ea-label" id="ea-accent-label">Accent · {generation.label}</span>
        <div className="ea-orbs" role="group" aria-labelledby="ea-accent-label">
          {GENERATIONS.map((item) => {
            const active = item.key === mode;
            return (
              <button
                key={item.key}
                type="button"
                className={`ea-orb${active ? ' is-active' : ''}`}
                onClick={() => setMode(item.key)}
                aria-pressed={active}
                style={{'--orb-hue': item.hue, '--orb-glow': item.glow}}
              >
                <span className="ea-orb-ball" aria-hidden="true">{item.icon}</span>
                <span className="ea-orb-label">{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default EmojiTranslator;